/**
 * AppICEMAP — RegisterScreen
 * Custom element: `<register-screen>`
 *
 * Full-screen registration form: name, email, password, confirm password.
 * Validates locally then calls Directus's public self-registration endpoint.
 * Tries to auto-login right after; if the project requires email
 * verification (this one does), that login is expected to fail, so the user
 * is sent to the login screen with a "check your email" notice instead.
 *
 * Events:
 *   auth:success   → { user }
 *   auth:close     → { }
 */

import { register, login } from "../../api/auth.js";
import { dispatch } from "../../store/index.js";
import { show as showToast } from "../shared/Toast.js";
import router from "../../router.js";
// Shadow DOM encapsulates styles — inject the global auth.css inside.
import authCss from "../../../css/components/auth.css?inline";
// Phosphor's `.ph` rules live at document scope and don't cross the shadow
// boundary either — without this the back arrow, logo pin and password-eye
// icons all render as blank boxes.
import iconCss from "../../../css/utilities/phosphor-shadow.css?inline";

const template = document.createElement("template");
template.innerHTML = `
  <div class="auth-screen">
    <button class="auth-screen__back" aria-label="Volver" data-action="back">
      <i class="ph ph-caret-left" aria-hidden="true"></i>
    </button>

    <div class="auth-screen__logo">
      <i class="ph ph-map-pin" aria-hidden="true"></i>
      <h1>AppICEMAP</h1>
      <p>Crear cuenta</p>
    </div>

    <form class="auth-form" novalidate>
      <div class="field" data-field="name">
        <label for="reg-name">Nombre</label>
        <input
          id="reg-name"
          type="text"
          class="input"
          autocomplete="name"
          placeholder="Tu nombre"
          required
        />
        <span class="field__error" role="alert" hidden></span>
      </div>

      <div class="field" data-field="email">
        <label for="reg-email">Correo electrónico</label>
        <input
          id="reg-email"
          type="email"
          class="input"
          autocomplete="email"
          placeholder="tu@correo.com"
          required
        />
        <span class="field__error" role="alert" hidden></span>
      </div>

      <div class="field" data-field="password">
        <label for="reg-password">Contraseña</label>
        <div class="input-group">
          <input
            id="reg-password"
            type="password"
            class="input"
            autocomplete="new-password"
            placeholder="Mínimo 8 caracteres"
            required
            minlength="8"
          />
          <button type="button" class="input-group__toggle" aria-label="Mostrar contraseña" data-action="toggle-password">
            <i class="ph ph-eye" aria-hidden="true"></i>
          </button>
        </div>
        <span class="field__error" role="alert" hidden></span>
      </div>

      <div class="field" data-field="confirm">
        <label for="reg-confirm">Confirmar contraseña</label>
        <input
          id="reg-confirm"
          type="password"
          class="input"
          autocomplete="new-password"
          placeholder="Repite la contraseña"
          required
        />
        <span class="field__error" role="alert" hidden></span>
      </div>

      <div class="field__error form-error" role="alert" hidden></div>

      <button type="submit" class="btn-block">
        <span class="btn__label">Crear cuenta</span>
        <span class="btn__spinner" aria-hidden="true"></span>
      </button>
    </form>

    <p class="auth-screen__footer">
      ¿Ya tienes cuenta?
      <a href="#login" data-action="go-login">Inicia sesión</a>
    </p>
  </div>
`;

export class RegisterScreen extends HTMLElement {
	constructor() {
		super();
		this.attachShadow({ mode: "open" });
		const style = document.createElement("style");
		style.textContent = `${iconCss}\n${authCss}`;
		this.shadowRoot.appendChild(style);
		this.shadowRoot.appendChild(template.content.cloneNode(true));

		this._form = this.shadowRoot.querySelector(".auth-form");
		this._submitBtn = this.shadowRoot.querySelector(".btn-block");
		this._formError = this.shadowRoot.querySelector(".form-error");
		this._nameField = this.shadowRoot.querySelector('[data-field="name"]');
		this._emailField = this.shadowRoot.querySelector('[data-field="email"]');
		this._passwordField = this.shadowRoot.querySelector(
			'[data-field="password"]',
		);
		this._confirmField = this.shadowRoot.querySelector(
			'[data-field="confirm"]',
		);
		this._nameInput = this.shadowRoot.querySelector("#reg-name");
		this._emailInput = this.shadowRoot.querySelector("#reg-email");
		this._passwordInput = this.shadowRoot.querySelector("#reg-password");
		this._confirmInput = this.shadowRoot.querySelector("#reg-confirm");
		this._toggleBtn = this.shadowRoot.querySelector(
			'[data-action="toggle-password"]',
		);
		this._passwordVisible = false;
	}

	connectedCallback() {
		this._form.addEventListener("submit", this._onSubmit.bind(this));
		this._toggleBtn.addEventListener(
			"click",
			this._onTogglePassword.bind(this),
		);
		this.shadowRoot
			.querySelector('[data-action="back"]')
			?.addEventListener("click", () => this._close());
		this.shadowRoot
			.querySelector('[data-action="go-login"]')
			?.addEventListener("click", (e) => {
				e.preventDefault();
				router.navigate("/login");
			});
	}

	disconnectedCallback() {
		this._form.removeEventListener("submit", this._onSubmit);
	}

	/* ── Public API ──────────────────────────────────────────────────────── */

	show() {
		this.style.display = "block";
		this._clearErrors();
		this._nameInput.value = "";
		this._emailInput.value = "";
		this._passwordInput.value = "";
		this._confirmInput.value = "";
		this._passwordVisible = false;
		this._passwordInput.type = "password";
		this._toggleBtn.textContent = "";
		const eye = document.createElement("i");
		eye.className = "ph ph-eye";
		eye.setAttribute("aria-hidden", "true");
		this._toggleBtn.appendChild(eye);
	}

	hide() {
		this.style.display = "none";
	}

	/* ── Private ─────────────────────────────────────────────────────────── */

	_onTogglePassword() {
		this._passwordVisible = !this._passwordVisible;
		this._passwordInput.type = this._passwordVisible ? "text" : "password";
		this._toggleBtn.textContent = "";
		const icon = document.createElement("i");
		icon.className = this._passwordVisible ? "ph ph-eye-slash" : "ph ph-eye";
		icon.setAttribute("aria-hidden", "true");
		this._toggleBtn.appendChild(icon);
	}

	async _onSubmit(e) {
		e.preventDefault();
		this._clearErrors();

		const name = this._nameInput.value.trim();
		const email = this._emailInput.value.trim();
		const password = this._passwordInput.value;
		const confirm = this._confirmInput.value;

		let hasError = false;

		if (!name) {
			this._showFieldError(this._nameField, "Ingresa tu nombre");
			hasError = true;
		}

		if (!email) {
			this._showFieldError(this._emailField, "Ingresa tu correo electrónico");
			hasError = true;
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			this._showFieldError(this._emailField, "Ingresa un correo válido");
			hasError = true;
		}

		if (!password) {
			this._showFieldError(this._passwordField, "Ingresa una contraseña");
			hasError = true;
		} else if (password.length < 8) {
			this._showFieldError(
				this._passwordField,
				"La contraseña debe tener al menos 8 caracteres",
			);
			hasError = true;
		}

		if (!confirm) {
			this._showFieldError(this._confirmField, "Confirma tu contraseña");
			hasError = true;
		} else if (password !== confirm) {
			this._showFieldError(this._confirmField, "Las contraseñas no coinciden");
			hasError = true;
		}

		if (hasError) return;

		this._setLoading(true);

		// Registering and logging in are two separate calls on purpose: this
		// project has "Public Registration" → "Verify Email" turned on in
		// Directus, so the account is created in an "unverified" state and the
		// immediate login attempt is EXPECTED to fail until the user clicks the
		// link Directus emails them. If both steps shared one try/catch, that
		// expected failure looked identical to registration itself failing —
		// "Error al crear la cuenta" — even though the account had just been
		// created successfully.
		try {
			await register({
				email,
				password,
				first_name: name,
			});
		} catch (err) {
			console.error("[RegisterScreen] Registration failed:", err);
			const errors = err?.errors;
			let msg = "Error al crear la cuenta";

			if (errors?.length > 0) {
				const first = errors[0];
				if (first.extensions?.code === "RECORD_NOT_UNIQUE") {
					msg = "El correo ya está registrado";
				} else {
					msg = first.message || msg;
				}
			}

			this._showFormError(msg);
			this._setLoading(false);
			return;
		}

		try {
			const user = await login(email, password);

			dispatch("SET_USER", {
				id: user.id,
				email: user.email,
				first_name: user.first_name,
				last_name: user.last_name,
				avatar: user.avatar,
				karma: user.karma ?? 0,
				strikes: user.strikes ?? 0,
				rehabilitations: user.rehabilitations ?? 0,
				is_limited: user.is_limited ?? false,
				// Needed by utils/permissions.js — without these the admin
				// check can never see anything and the delete actions stay hidden
				role: user.role ?? null,
				policies: user.policies ?? null,
				admin_access: user.admin_access ?? undefined,
			});

			this.dispatchEvent(
				new CustomEvent("auth:success", {
					detail: { user },
					bubbles: true,
					composed: true,
				}),
			);

			this.hide();
			router.navigate("/");
		} catch {
			// The account exists — only the auto-login didn't go through, most
			// likely because it's pending email verification. Send them to
			// login (not back into the form) with an explanation instead of
			// the raw "Invalid user credentials" error.
			showToast(
				"Cuenta creada. Revisa tu correo y confirma tu cuenta para poder iniciar sesión.",
				"success",
				6000,
			);
			this.hide();
			router.navigate("/login");
		} finally {
			this._setLoading(false);
		}
	}

	_showFieldError(fieldEl, message) {
		fieldEl.classList.add("field--error");
		const errorEl = fieldEl.querySelector(".field__error");
		if (errorEl) {
			errorEl.textContent = message;
			errorEl.hidden = false;
		}
	}

	_showFormError(message) {
		this._formError.textContent = message;
		this._formError.hidden = false;
	}

	_clearErrors() {
		[
			this._nameField,
			this._emailField,
			this._passwordField,
			this._confirmField,
		].forEach((f) => f?.classList.remove("field--error"));
		const errors = this.shadowRoot.querySelectorAll(".field__error");
		errors.forEach((e) => {
			e.textContent = "";
			e.hidden = true;
		});
		this._formError.hidden = true;
	}

	_setLoading(loading) {
		if (loading) {
			this._submitBtn.classList.add("is-loading");
			this._submitBtn.disabled = true;
		} else {
			this._submitBtn.classList.remove("is-loading");
			this._submitBtn.disabled = false;
		}
	}

	_close() {
		this.hide();
		this.dispatchEvent(
			new CustomEvent("auth:close", { bubbles: true, composed: true }),
		);
	}
}

if (!customElements.get("register-screen")) {
	customElements.define("register-screen", RegisterScreen);
}
